/**
 * Runtime loader for the baked **blood-splatter** atlas (Blood splatter, PRD #372,
 * Slice A) - the 7 single-frame item statics declared in the `bloodArt` content
 * registry and baked (or stubbed) into `assets/baked/blood/blood.{png,json}` on the
 * single-frame `decodeStaticTile` path (the `statics` recipe, one shared atlas
 * keyed by the friendly names `blood-0..blood-6`).
 *
 * Glob'd rather than statically imported so a host whose baked atlas is missing (or
 * whose real `npm run convert` warn-skipped an id with no art) degrades **gracefully
 * to absent** - the {@link ./bloodLayer.BloodLayer} then draws that splat with its
 * mandatory Graphics-primitive fallback (a small irregular dark-red blob) instead of
 * crashing. The importable set is filled by walking the SAME registry the
 * converter/stub bake from, so what can be loaded here never drifts from what is
 * baked.
 */
import { Assets, Rectangle, Texture } from 'pixi.js';
import { bloodArt as bloodDefs, type BloodDefinition } from '../sim/content';
import type { BakedSingleAtlasStaticsManifest } from './bakedManifest';

/** Blood decal textures keyed by registry name (`blood-0`..`blood-6`). Partial: unbaked ids absent. */
export type BloodTextures = Map<string, Texture>;

// The baked blood atlas + manifest, discovered like the effects/statics globs: Vite
// resolves these at build/test time against `assets/baked/blood/`, which the stub
// (or the real converter) fills by walking the same `bloodArt` registry.
const bloodManifests = import.meta.glob('../../assets/baked/blood/blood.json', {
  eager: true,
  import: 'default',
}) as Record<string, BakedSingleAtlasStaticsManifest>;
const bloodAtlasUrls = import.meta.glob('../../assets/baked/blood/blood.webp', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

/**
 * Load the baked blood atlas and slice it into one texture per registry name. An
 * absent atlas/manifest (unbaked host) simply yields an **empty** map - never a
 * crash - and the layer falls back to Graphics primitives for every splat. Sampled
 * `nearest` to keep the pixel art crisp, matching every other pixel-art load path.
 */
export async function loadBloodTextures(): Promise<BloodTextures> {
  const textures: BloodTextures = new Map();
  const manifest = Object.values(bloodManifests)[0];
  const url = Object.values(bloodAtlasUrls)[0];
  if (!manifest || !url) return textures;

  const atlas = await Assets.load<Texture>(url);
  atlas.source.scaleMode = 'nearest';
  // Only surface names the registry knows, so a rogue manifest entry can't leak in.
  const known = new Set(bloodDefs.all().map((d: BloodDefinition) => d.id));
  for (const s of manifest.statics) {
    if (!known.has(s.name)) continue;
    textures.set(s.name, new Texture({ source: atlas.source, frame: new Rectangle(s.x, s.y, s.w, s.h) }));
  }
  return textures;
}
